import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { MapPin, Trash2, X } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { makeBaseLayer } from "@/lib/mapBase";

const pin = () => L.divIcon({ className: "", html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:#dc2626;border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`, iconAnchor: [9, 16] });
const bpin = () => L.divIcon({ className: "", html: `<div style="width:14px;height:14px;border-radius:50% 50% 50% 0;background:#1e40af;border:2px solid #fff;transform:rotate(-45deg)"></div>`, iconAnchor: [7, 13] });

// Full marker/draw map for internal locations — click to add, drag to move, list beside. Controlled via `locations`.
export function LocationMapEditor({ locations = [], canWrite = true, onChanged }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const baseRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [area, setArea] = useState(null);
  const [mapCfg, setMapCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const [sat, setSat] = useState(false);
  const [pending, setPending] = useState(null);
  const [pname, setPname] = useState("");
  const [pcode, setPcode] = useState("");
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/buildings").catch(() => ({ data: [] })), api.get("/settings/map").catch(() => ({ data: null })), api.get("/settings/service-area").catch(() => ({ data: {} }))])
      .then(([b, m, s]) => { setBuildings(b.data || []); setMapCfg(m.data || null); setArea((s.data && s.data.polygon) || null); setReady(true); });
  }, []);

  useEffect(() => {
    if (!ready || !el.current || mapRef.current) return;
    const withCoord = locations.find((l) => l.lat != null);
    const c = withCoord ? [withCoord.lat, withCoord.lng] : (buildings[0] && buildings[0].lat != null ? [buildings[0].lat, buildings[0].lng] : [52.11, 5.18]);
    const map = L.map(el.current, { attributionControl: false }).setView(c, 14);
    mapRef.current = map;
    baseRef.current = makeBaseLayer(mapCfg, false).addTo(map);
    buildings.forEach((b) => { if (b.lat != null) L.marker([b.lat, b.lng], { icon: bpin() }).addTo(map).bindPopup(b.name); });
    if (area && area.length >= 3) { try { map.fitBounds(L.polygon(area).getBounds().pad(0.12)); } catch (e) {} }
    if (canWrite) {
      let pt = null; let dll = null;
      const openAdd = (latlng) => { setPname(""); setPcode(""); setPending({ lat: Number(latlng.lat.toFixed(6)), lng: Number(latlng.lng.toFixed(6)) }); };
      map.on("mousedown", (e) => { dll = e.latlng; clearTimeout(pt); pt = setTimeout(() => openAdd(dll), 500); });
      map.on("mouseup", () => clearTimeout(pt));
      map.on("movestart", () => clearTimeout(pt));
      map.on("contextmenu", (e) => { try { L.DomEvent.preventDefault(e.originalEvent); } catch (_) {} openAdd(e.latlng); });
    }
    setTimeout(() => map.invalidateSize(), 200);
  }, [ready, buildings, canWrite]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(mapCfg, sat).addTo(map);
    baseRef.current.bringToBack();
    if (!sat && mapCfg && mapCfg.mode === "static" && mapCfg.bounds && !locations.some((l) => l.lat != null)) { try { map.fitBounds(mapCfg.bounds); } catch (e) {} }
  }, [mapCfg, sat]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};
    locations.forEach((l) => {
      if (l.lat == null) return;
      const m = L.marker([l.lat, l.lng], { icon: pin(), draggable: canWrite }).addTo(map).bindPopup(`${l.name}${l.code ? " · " + l.code : ""}`);
      if (canWrite) m.on("dragend", async (e) => { const { lat, lng } = e.target.getLatLng(); try { await api.put(`/locations/${l.id}`, { name: l.name, code: l.code || "", lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }); onChanged && onChanged(); } catch (er) {} });
      markersRef.current[l.id] = m;
    });
  }, [locations, canWrite]); // eslint-disable-line

  const savePending = async () => {
    if (!pname.trim()) { toast.error("Geef een naam op"); return; }
    setSaving(true);
    try { await api.post("/locations", { name: pname.trim(), code: pcode.trim(), lat: pending.lat, lng: pending.lng }); toast.success("Locatie toegevoegd"); setPending(null); onChanged && onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const confirmDelete = async () => { try { await api.delete(`/locations/${delTarget.id}`); toast.success("Locatie verwijderd"); setDelTarget(null); onChanged && onChanged(); } catch (e) { setDelTarget(null); } };

  const withCoords = locations.filter((l) => l.lat != null);
  return (
    <div data-testid="location-map-editor" className="mt-4">
      {canWrite && <div className="mb-2 text-xs text-slate-400">Klik-en-houd (rechtsklik / long-press) op de kaart om een locatie toe te voegen, sleep een marker om te verplaatsen.</div>}
      <div className="grid gap-4 lg:grid-cols-4">
        <div className="max-h-[420px] space-y-2 overflow-y-auto lg:col-span-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Op kaart ({withCoords.length})</div>
          {withCoords.length === 0 && <div className="text-xs text-slate-400">Nog geen locaties met coördinaten.</div>}
          {withCoords.map((l) => (
            <div key={l.id} data-testid={`locmap-item-${l.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-2.5">
              <div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-900">{l.name}</div><div className="truncate text-[11px] text-slate-500">{l.code ? l.code + " · " : ""}{Number(l.lat).toFixed(4)}, {Number(l.lng).toFixed(4)}</div></div>
              {canWrite && <button data-testid={`locmap-del-${l.id}`} onClick={() => setDelTarget(l)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        <MapShell heightClass="h-[420px] lg:col-span-3" testid="location-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
          <button data-testid="location-satellite-toggle" onClick={() => setSat((s) => !s)} className={`absolute left-3 top-3 z-[1001] rounded-lg border px-3 py-1.5 text-xs font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>{sat ? "Satelliet aan" : "Satelliet"}</button>
          <div ref={el} data-testid="location-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
        </MapShell>
      </div>

      {pending && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setPending(null)}>
          <div data-testid="location-add-dialog" className="w-full max-w-sm rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="flex items-center gap-2 font-display font-bold text-slate-900"><MapPin className="h-4 w-4 text-red-600" /> Locatie toevoegen</h3>
              <button onClick={() => setPending(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 p-6">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Naam</label>
                <input data-testid="location-name-input" autoFocus value={pname} onChange={(e) => setPname(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") savePending(); }} placeholder="Bijv. Kazerne Noord" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Code (optioneel)</label>
                <input data-testid="location-code-input" value={pcode} onChange={(e) => setPcode(e.target.value)} placeholder="Bijv. KZN-N" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
              <div className="text-[11px] text-slate-400">Locatie: {pending.lat}, {pending.lng}</div>
              <div className="flex gap-2 pt-1">
                <button data-testid="location-save-btn" onClick={savePending} disabled={saving} className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60">Opslaan</button>
                <button onClick={() => setPending(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuleren</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDelTarget(null)}>
          <div data-testid="location-delete-dialog" className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-slate-900">Locatie verwijderen?</h3>
            <p className="mt-1 text-sm text-slate-500">"{delTarget.name}" wordt permanent verwijderd.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="location-confirm-delete" onClick={confirmDelete} className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-500">Verwijderen</button>
              <button onClick={() => setDelTarget(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
